import React from "react";

import _ from "lodash";
import { FormCheck } from "react-bootstrap";
import {
  type Control,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  type UseControllerProps,
} from "react-hook-form";

import { useScopedController } from "@/components/ScopedForm/hooks";
import RHErrorMessage from "@/containers/RHErrorMessage";

interface PropsType<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
> extends UseControllerProps<TFieldValues, TName, TTransformedValues> {
  control: Control<TFieldValues, any, TTransformedValues>;

  inactiveValue?: FieldPathValue<TFieldValues, TName> extends Array<unknown>
    ? FieldPathValue<TFieldValues, TName>[number]
    : FieldPathValue<TFieldValues, TName>;
  value?: FieldPathValue<TFieldValues, TName> extends Array<unknown>
    ? FieldPathValue<TFieldValues, TName>[number]
    : FieldPathValue<TFieldValues, TName>;

  type?: "checkbox" | "radio" | "switch";

  id?: string;
  label?: React.ReactNode;
  inline?: boolean;

  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;

  validate?: boolean;
  isValid?: boolean;
  isInvalid?: boolean;

  hideError?: boolean;
  namespace?: string;
  errorClassName?: string;
}

const isChecked = (givenValue: any, fieldValue: any) => {
  if (_.isArray(givenValue)) {
    return givenValue.includes(fieldValue);
  }

  return givenValue === fieldValue;
};

export default function RHCheck<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  control,
  name,

  value = true as FieldPathValue<TFieldValues, TName>,
  inactiveValue = false as FieldPathValue<TFieldValues, TName>,

  type = "checkbox",

  onChange,

  validate = true,
  isValid,
  isInvalid,

  hideError = false,
  namespace,
  errorClassName,
  ...props
}: PropsType<TFieldValues, TName, TTransformedValues>) {
  const {
    field,
    formState: { isSubmitted },
    fieldState: { invalid, isTouched, error },
  } = useScopedController({ control, name });

  async function handleChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const trgt = ev.target;
    const givenValue = field.value;

    if (trgt.checked) {
      if (_.isArray(givenValue)) {
        field.onChange(type === "radio" ? [value] : [...givenValue, value]);
      } else {
        field.onChange(value);
      }
    } else {
      if (_.isArray(givenValue)) {
        field.onChange(givenValue.filter((v: FieldPathValue<TFieldValues, TName>) => v !== value));
      } else {
        field.onChange(inactiveValue);
      }
    }

    if (onChange) onChange(ev);
  }

  return (
    <>
      <FormCheck
        type={type}
        checked={isChecked(field.value, value)}
        {...props}
        {...field}
        value={value}
        onChange={handleChange}
        isValid={isValid ?? (validate && (isTouched || isSubmitted) && !invalid)}
        isInvalid={isInvalid ?? (validate && (isTouched || isSubmitted) && invalid)}
      />

      {validate && !hideError && (
        <RHErrorMessage namespace={namespace} errorMessage={error} className={errorClassName} />
      )}
    </>
  );
}
